import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import LoadingSpinner from "../components/LoadingSpinner";
import axios from "axios";
import "../assets/css/results-view.css";

// Print assets – same as ApplicationSuccess
import logo from "../assets/images/luth-logo-192.png";
import logo1 from "../assets/images/luth-logo-16x16.png";
import logoprint from "../assets/images/luth-logo-small.png";

export default function RegisteredCoursesView() {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter states
  const [selectedLevel, setSelectedLevel] = useState("All");
  const [selectedSemester, setSelectedSemester] = useState("All");

  const API =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_BASE_URL
      : "http://localhost:3003";
  const matricNo = localStorage.getItem("matricNo");

  // For printing
  const printRef = useRef();

  // Fetch registrations with current filters
  const fetchRegs = async () => {
    try {
      setLoading(true);
      setError("");
      const params = { matricNo };
      if (selectedLevel !== "All") params.level = selectedLevel;
      if (selectedSemester !== "All") params.semester = selectedSemester;

      const res = await axios.get(`${API}/api/registrations/view`, { params });
      setRegs(res.data || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load registered courses.");
    } finally {
      setLoading(false);
    }
  };

  // Refetch when filters change
  useEffect(() => {
    fetchRegs();
  }, [selectedLevel, selectedSemester]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- PRINT HANDLER (exactly as in ApplicationSuccess) ----------
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const win = window.open("", "", "height=700,width=900");
    win.document.write("<html><head><title>Print Course Registration</title>");
    win.document.write(`
      <style>
        body {
          font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif;
          margin: 0;
          font-size: 12px;
        }
        .watermark {
          display: block;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0.1;
          width: 300px;
          z-index: -1;
        }
        .logo-print {
          width: 80px;
          height: auto;
          object-fit: cover;
          border: 1px solid #ccc;
        }
        .section-title {
          font-weight: bold;
          margin-top: 20px;
          font-size: 1.2rem;
          border-bottom: 2px solid #ccc;
          padding-bottom: 5px;
        }
        .print-area {
          position: relative;
        }
        .print-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        .print-title {
          color: green;
          text-align: center;
          font-size: 1.1rem;
          margin-bottom: 0.2rem;
        }
        .print-subtitle {
          color: #333;
          text-align: center;
          font-size: 1rem;
          margin-bottom: 0.2rem;
        }
        .student-info {
          margin-bottom: 20px;
          padding: 10px;
          background: #f9f9f9;
          border-left: 4px solid #28a745;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        th, td {
          padding: 8px;
          border: 1px solid #ddd;
          text-align: left;
        }
        th {
          background-color: #28a745;
          color: white;
        }
        p.note {
          margin-top: 1.5rem;
          font-style: italic;
        }
      </style>
    `);
    win.document.write("</head><body>");
    win.document.write(printContents);
    win.document.close();
    win.focus();
    win.onload = () => {
      win.print();
      win.close();
    };
  };

  return (
    <div className="result-page" style={{ marginTop: "-130px" }}>
      <Sidebar />
      <div className="result-main">
        <main className="results-view-page">
          <h1>Your Registered Courses</h1>

          {/* Filter Controls + Print Button */}
          <div
            className="filters"
            style={{
              marginBottom: "20px",
              display: "flex",
              gap: "20px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div>
              <label htmlFor="level">Level: </label>
              <select
                id="level"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <option value="All">All</option>
                <option value="ND1">ND1</option>
                <option value="ND2">ND2</option>
                <option value="HND1">HND1</option>
                <option value="HND2">HND2</option>
              </select>
            </div>
            <div>
              <label htmlFor="semester">Semester: </label>
              <select
                id="semester"
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
              >
                <option value="All">All</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
            </div>

            {/* Print Button */}
            <button
              onClick={handlePrint}
              disabled={regs.length === 0}
              style={{
                padding: "8px 16px",
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: regs.length === 0 ? "not-allowed" : "pointer",
                opacity: regs.length === 0 ? 0.6 : 1,
                marginLeft: "auto",
              }}
            >
              🖨️ Print Course Registration
            </button>
          </div>

          {loading && <LoadingSpinner />}
          {error && <p className="error">{error}</p>}
          {!loading && regs.length === 0 && (
            <p className="message">
              {selectedLevel !== "All" || selectedSemester !== "All"
                ? "No courses registered for the selected filters."
                : "You have not registered for any courses."}
            </p>
          )}
          {!loading && regs.length > 0 && (
            <table className="results-table">
              <thead>
                <tr>
                  <th>Course Code</th>
                  <th>Title</th>
                  <th>Units</th>
                  <th>Registered On</th>
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => (
                  <tr key={r.RegistrationID}>
                    <td>{r.CourseCode}</td>
                    <td>{r.CourseTitle}</td>
                    <td>{r.CreditUnits}</td>
                    <td>{new Date(r.RegisteredAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </main>
      </div>

      {/* ---------- HIDDEN PRINT CONTENT (exact style from ApplicationSuccess) ---------- */}
      <div ref={printRef} style={{ display: "none" }}>
        <div className="print-area">
          {/* Watermark */}
          <img src={logoprint} alt="LUTH watermark" className="watermark" />

          {/* Header with logos */}
          <div className="print-header">
            <img src={logo} alt="LUTH Logo" className="logo-print" />
            <div style={{ textAlign: "center", flex: 1 }}>
              <h2 className="print-title">
                LAGOS UNIVERSITY TEACHING HOSPITAL
              </h2>
              <h3 className="print-subtitle">
                SCHOOLS OF NURSING, MIDWIFERY, AND HEALTH TECHNOLOGY
              </h3>
              <h4 style={{ color: "#28a745", marginTop: "5px" }}>
                OFFICIAL COURSE REGISTRATION SLIP
              </h4>
            </div>
            <img src={logo1} alt="LUTH small logo" className="logo-print" />
          </div>

          {/* Student & Filter Info */}
          <div className="student-info">
            <p>
              <strong>Matric Number:</strong> {matricNo}
            </p>
            <p>
              <strong>Level Filter:</strong>{" "}
              {selectedLevel === "All" ? "All Levels" : selectedLevel}
            </p>
            <p>
              <strong>Semester Filter:</strong>{" "}
              {selectedSemester === "All"
                ? "All Semesters"
                : `Semester ${selectedSemester}`}
            </p>
            <p>
              <strong>Generated On:</strong> {new Date().toLocaleString()}
            </p>
          </div>

          {/* Courses Table – exactly same as main view */}
          <table>
            <thead>
              <tr>
                <th>Course Code</th>
                <th>Course Title</th>
                <th>Credit Units</th>
                <th>Registration Date</th>
              </tr>
            </thead>
            <tbody>
              {regs.map((r) => (
                <tr key={r.RegistrationID}>
                  <td>{r.CourseCode}</td>
                  <td>{r.CourseTitle}</td>
                  <td>{r.CreditUnits}</td>
                  <td>{new Date(r.RegisteredAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer note */}
          <p className="note">
            This is an electronically generated document. No signature is
            required.
          </p>
        </div>
      </div>
    </div>
  );
}
